import { useStore } from 'hooks/useStore';
import { unconstrainedForecastInterface } from 'Interfaces/FDPInterfaces/unconstrainedForecast';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { constrainedForecastService } from 'services/fdp/constrainedForecastService';
import { unconstrainedForecastService } from 'services/fdp/unconstrainedForecastService';
import { convertIsoToDateString } from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';

type valueTypeInterface = 'saved' | 'old' | 'forecast';

export const useConstrainedForecastEdit = () => {
  const {
    UserStore: { userModulesRoles },
    NavbarStore: { isNavigationRestricted, setisNavigationRestricted },
    ConstrainedForecastViewStore: { fetchBulkUpdateNDCs, fetchForecastProgress },
    ConstrainedForecastEditStore: {
      isConceptLoading,
      conceptDetails,
      contractDetails,
      directcurrentNDCIndex,
      indirectcurrentNDCIndex,
      constrainedForecastEdit,
      oldconstrainedForecastEdit,
      directNDCheading,
      indirectNDCheading,
      isConstrainedForecastDataLoading,
      isInvoiceSalesLoading,
      invoiceSalesSummary,
      isConstrainedForecastNotesDataLoading,
      isConstrainedNoteSaving,
      ConstrainedForecastNotesData,
      isDirectMode,
      isConstrainedNoteSaved: _isConstrainedNoteSaved,
      setIsDirectMode,
      fetchForecastGFCNotes,
      setDirectCurrentTabIndex,
      setIndirectCurrentTabIndex,
      handleGetConceptDetails,
      saveForecastNote,
      updateConstrainedForecastEdit,
      updateOldValues,
      setNoteData,
      removeNoteData,
      resetStore,
    },
  } = useStore();

  const navigate = useNavigate();
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [singleSaveModal, setSingleSaveModal] = useState<boolean>(false);
  const [saveAllModal, setSaveAllModal] = useState<boolean>(false);
  const [savingForCategory, setSavingForCategory] = useState<boolean>(false);
  const [savingSingle, setSavingSingle] = useState<boolean>(false);
  const [isSavingSingle, setIsSavingSingle] = useState<boolean>(false);
  const [_usingOldValuesNdcMapping, _setUsingOldValuesNdcMapping] = useState<Set<string>>(new Set());
  const [savingAll, setSavingAll] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  // const [isDirectMode, setIsDirectMode] = useState<boolean>(true);
  const [ndcValueTypeUsing, setNdcValueTypeUsing] = useState<
    Map<string, 'saved' | 'old' | 'forecast'>
  >(new Map());
  const [ndcUsingPercentage, setNdcUsingPercentage] = useState<Set<string>>(new Set());
  const [copyRowInfo, setCopyRowInfo] = useState<Set<string>>(new Set());
  const [isNoteEdited, setIsNoteEdited] = useState<Map<number, boolean>>(new Map());
  const [isNoteDeleted, setIsNoteDeleted] = useState<Map<number, boolean>>(new Map());
  const [_currentFocusedRow, _setCurrentFocusedRow] = useState<Set<string>>(new Set());
  const directInputFocus = useRef<HTMLInputElement[]>([]);
  const indirectInputFocus = useRef<HTMLInputElement[]>([]);
  const [directFocused, setDirectFocused] = useState<number | null>(null);
  const [indirectFocused, setIndirectFocused] = useState<number | null>(null);

  const { gfc } = useParams();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchForecastGFCNotes(
      isDirectMode
        ? directNDCheading[directcurrentNDCIndex]?.ndc
        : indirectNDCheading[indirectcurrentNDCIndex]?.ndc,
      signal
    );
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directcurrentNDCIndex, indirectcurrentNDCIndex, isDirectMode]);

  useEffect(() => {
    // getting the constrained forecast edit details
    handleGetConceptDetails(Number(gfc));

    if (directNDCheading && directNDCheading.length === 0) {
      setIsDirectMode(false);
    }

     
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
    setisNavigationRestricted(true);

    return () => {
      setisNavigationRestricted(false);
      resetStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFutureMonthsEdit = () => {
    const arr: { label: string; dateString: string }[] = [];
    const dt = new Date();
    const startMonth = dt.getMonth();
    const startYear = dt.getFullYear();
    const monthShortHand = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    for (let i = 0; i < 15; i++) {
      const month = (startMonth + i + 1) % 12;
      const year = startYear + Math.floor((startMonth + i + 1) / 12);
      arr.push({
        label: `${monthShortHand[month]}-${year}`,
        dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
      });
    }

    return arr;
  };
  const forecastingMonthsEdit: { label: string; dateString: string }[] = getFutureMonthsEdit();

  const handleCcancel = () => {
    // navigate(`/fdp/constrained_forecast?gfc=${conceptDetails?.gfc}`);
    if (
      isNavigationRestricted &&
      constrainedForecastEdit.length > 0 &&
      JSON.stringify(constrainedForecastEdit) !== JSON.stringify(oldconstrainedForecastEdit)
    ) {
      if (
        window.confirm(`Are you sure want to navigate?,
Unsaved changes will Be discarded!`) // intensional removal of indentation for getting proper formatted message in the alert
      ) {
        navigate(`/fdp/constrained_forecast?gfc=${conceptDetails?.gfc}`);
      }
    } else {
      navigate(`/fdp/constrained_forecast?gfc=${conceptDetails?.gfc}`);
    }
  };

  const handleDirectNDCTabChange = (e: any) => {
    setDirectCurrentTabIndex(e.index);
  };

  const handleIndirectNDCTabChange = (e: any) => {
    setIndirectCurrentTabIndex(e.index);
  };

  // utility function to get sales details
  const getInvoiceSalesDetails = (ndc: string, want: string) => {
    if (invoiceSalesSummary.length === 0) return;

    const ndcSummary = invoiceSalesSummary.filter(item => item.NDC11 === ndc);

    if (ndcSummary.length === 0) {
      return 'NA';
    } else {
      if (want === 'ytd' && ndcSummary[0].YTDAverage !== null) {
        return ndcSummary[0].YTDAverage;
      } else if (want === '3ma' && ndcSummary[0].ThreeMonthAverage !== null) {
        return ndcSummary[0].ThreeMonthAverage;
      } else if (want === 'pm' && ndcSummary[0].LastMonthAverage !== null) {
        return ndcSummary[0].LastMonthAverage;
      } else {
        return 'NA';
      }
    }
  };

  function _isNumericval(value: string) {
    return /^[0-9]{1,9}$/.test(value);
  }

  function isNumeric(value: string) {
    // Allow values from -100 to 100, with optional decimals
    return /^-?[1-9][0-9]?$|^-?100$/.test(value) || /^(0|[1-9][0-9]{0,7})$/.test(value);
  }

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  const _checkIfValuesAreEditedForListOfNdcs = (ndcs: string[]) => {
    let isEdited = false;

    oldconstrainedForecastEdit.forEach((item, index) => {
      if (ndcs.includes(item.NDC11)) {
        item.forecast_data.forEach((forecastData, forecastDataIndex) => {
          if (ndcValueTypeUsing.get(item.NDC11) === 'saved') {
            if (
              forecastData.savedConstraintForecast !=
              constrainedForecastEdit[index].forecast_data[forecastDataIndex]
                .savedConstraintForecast
            ) {
              isEdited = true;
            }
          } else if (ndcValueTypeUsing.get(item.NDC11) === 'old') {
            if (
              forecastData.OldConstrainedForecast !=
              constrainedForecastEdit[index].forecast_data[forecastDataIndex].OldConstrainedForecast
            ) {
              isEdited = true;
            }
          } else {
            if (
              forecastData.ConstrainedForecast !=
              constrainedForecastEdit[index].forecast_data[forecastDataIndex].ConstrainedForecast
            ) {
              isEdited = true;
            }
          }
        });
      }
    });

    return isEdited;
  };

  const saveSingleConstrainedForecast = async () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex].ndc
      : indirectNDCheading[indirectcurrentNDCIndex].ndc;

    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      UnconstrainedForecast: number;
      ConstrainedForecast: number;
      Adjustment: number;
      Notes: string;
    }[] = [];

    const forecasts = constrainedForecastEdit.filter(item => item.NDC11 === ndc);

    if (forecasts.length !== 1) {
      return;
    }

    const planning_period = getCurrentDate();

    for (const item of forecasts[0].forecast_data) {
      const hasNDCInPayload = payload.some(
        itm => itm.NDC11 === ndc && itm.ForecastPeriod === item.ForecastPeriod
      );

      let constrainedForecast = item.ConstrainedForecast;
      if (ndcValueTypeUsing.get(ndc) === 'saved') {
        constrainedForecast = item.savedConstraintForecast;
      } else if (ndcValueTypeUsing.get(ndc) === 'old') {
        constrainedForecast = item.OldConstrainedForecast;
      }

      if (!hasNDCInPayload) {
        const obj = {
          NDC11: ndc,
          PlanningPeriod: planning_period,
          ForecastPeriod: item.ForecastPeriod,
          UnconstrainedForecast: item.UnconstrainedForecastID,
          ConstrainedForecast: Number(constrainedForecast),
          Adjustment: Number(constrainedForecast) - Number(item.UnconstrainedForecast),
          Notes: 'Constrained Forecast Single Save',
          SellingCategory: item.SellingCategory,
        };
        payload.push(obj);
      }
    }
    try {
      setSavingSingle(true);
      await constrainedForecastService.saveConstrainedForecast(payload);
      updateOldValues();
    } catch (_error) {
      toast.error('Constrained forecast could not be saved, please try again later');
    } finally {
      setSingleSaveModal(false);
      setSavingSingle(false);
      fetchBulkUpdateNDCs();
      fetchForecastProgress();
      setCopyRowInfo(new Set());
    }
  };

  const saveAllConstrainedForecast = async () => {
    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      UnconstrainedForecast: number;
      ConstrainedForecast: number;
      Adjustment: number;
      Notes: string;
    }[] = [];
    const planning_period = getCurrentDate();
    for (const ndcData of constrainedForecastEdit) {
      ndcData.forecast_data.forEach(forecast => {
        const hasNDCInPayload = payload.some(
          itm => itm.NDC11 === ndcData.NDC11 && itm.ForecastPeriod === forecast.ForecastPeriod
        );

        let constrainedForecast = forecast.ConstrainedForecast;
        if (ndcValueTypeUsing.get(ndcData.NDC11) === 'saved') {
          constrainedForecast = forecast.savedConstraintForecast;
        } else if (ndcValueTypeUsing.get(ndcData.NDC11) === 'old') {
          constrainedForecast = forecast.OldConstrainedForecast;
        }

        if (!hasNDCInPayload) {
          const obj = {
            NDC11: ndcData.NDC11,
            PlanningPeriod: planning_period,
            ForecastPeriod: forecast.ForecastPeriod,
            UnconstrainedForecast: forecast.UnconstrainedForecastID,
            ConstrainedForecast: Number(constrainedForecast),
            Adjustment: Number(constrainedForecast) - Number(forecast.UnconstrainedForecast),
            Notes: 'Constrained Forecast Save All',
            SellingCategory: forecast.SellingCategory,
          };
          payload.push(obj);
        }
      });
    }
    try {
      setSavingAll(true);
      await constrainedForecastService.saveConstrainedForecast(payload);
      updateOldValues();
    } catch (_error) {
      toast.error('Constrained Forecast could not be saved, please try again later');
    } finally {
      // bulk update list + progress are refreshed by the view screen on mount
      navigate(`/fdp/constrained_forecast?gfc=${conceptDetails?.gfc}`, {
        state: { from: 'finish', gfc: conceptDetails?.gfc },
      });
      setSaveAllModal(false);
      setSavingAll(false);
      setCopyRowInfo(new Set());
    }
  };

  const saveForCategory = async () => {
    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      UnconstrainedForecast: number;
      ConstrainedForecast: number;
      Adjustment: number;
      Notes: string;
    }[] = [];
    const ndcs: string[] = [];

    const planning_period = getCurrentDate();
    for (const ndcData of constrainedForecastEdit) {
      if (isDirectMode && ndcData.SellingCategory === 'Direct') {
        ndcData.forecast_data.forEach(forecast => {
          const hasNDCInPayload = payload.some(
            itm => itm.NDC11 === ndcData.NDC11 && itm.ForecastPeriod === forecast.ForecastPeriod
          );

          let constrainedForecast = forecast.ConstrainedForecast;
          if (ndcValueTypeUsing.get(ndcData.NDC11) === 'saved') {
            constrainedForecast = forecast.savedConstraintForecast;
          } else if (ndcValueTypeUsing.get(ndcData.NDC11) === 'old') {
            constrainedForecast = forecast.OldConstrainedForecast;
          }

          if (!hasNDCInPayload) {
            ndcs.push(ndcData.NDC11);
            const obj = {
              NDC11: ndcData.NDC11,
              PlanningPeriod: planning_period,
              ForecastPeriod: forecast.ForecastPeriod,
              UnconstrainedForecast: forecast.UnconstrainedForecastID,
              ConstrainedForecast: Number(constrainedForecast),
              Adjustment: Number(constrainedForecast) - Number(forecast.UnconstrainedForecast),
              Notes: 'Constrained Forecast Save All',
              SellingCategory: forecast.SellingCategory,
            };
            payload.push(obj);
          }
        });
      } else if (!isDirectMode && ndcData.SellingCategory === 'Indirect') {
        ndcData.forecast_data.forEach(forecast => {
          const hasNDCInPayload = payload.some(
            itm => itm.NDC11 === ndcData.NDC11 && itm.ForecastPeriod === forecast.ForecastPeriod
          );

          let constrainedForecast = forecast.ConstrainedForecast;
          if (ndcValueTypeUsing.get(ndcData.NDC11) === 'saved') {
            constrainedForecast = forecast.savedConstraintForecast;
          } else if (ndcValueTypeUsing.get(ndcData.NDC11) === 'old') {
            constrainedForecast = forecast.OldConstrainedForecast;
          }

          if (!hasNDCInPayload) {
            ndcs.push(ndcData.NDC11);
            const obj = {
              NDC11: ndcData.NDC11,
              PlanningPeriod: planning_period,
              ForecastPeriod: forecast.ForecastPeriod,
              UnconstrainedForecast: forecast.UnconstrainedForecastID,
              ConstrainedForecast: Number(constrainedForecast),
              Adjustment: Number(constrainedForecast) - Number(forecast.UnconstrainedForecast),
              Notes: 'Constrained Forecast Save All',
              SellingCategory: forecast.SellingCategory,
            };
            payload.push(obj);
          }
        });
      }
    }

    try {
      setIsSavingSingle(true);
      await constrainedForecastService.saveConstrainedForecast(payload);
      updateOldValues();
    } catch (_error) {
      // console.log("saveForCategory=> ", error);
      toast.error('Constrained Forecast could not be saved, please try again later');
    } finally {
      setSavingForCategory(prev => !prev);
      setIsSavingSingle(false);
      fetchBulkUpdateNDCs();
      fetchForecastProgress();
      setCopyRowInfo(new Set());
    }
  };

  const handleNoteAdd = (value: string) => {
    try {
      if (value.trim() === '') {
        toast.warning('Cannot save an empty note!!');
      } else if (conceptDetails && conceptDetails.gfc) {
        saveForecastNote(
          isDirectMode
            ? directNDCheading[directcurrentNDCIndex].ndc
            : indirectNDCheading[indirectcurrentNDCIndex].ndc,
          value,
          'constrained'
        );
      }
    } catch (_error) {
      toast("Note counldn't be saved, please try again later");
    }
  };

  const handleEditNote = async (id: number, note: string) => {
    try {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, true);
        } else {
          temp.set(id, true);
        }
        return temp;
      });
      const data = await unconstrainedForecastService.updateNote(id, note);
      if (data.status) {
        ConstrainedForecastNotesData.map(item => {
          if (item.ID === id) {
            item.CreatedDate = convertIsoToDateString(data.lastUpdatedDate) || '';
          }
        });
        toast.success('Note Updated successfully.');
      }
    } catch (_error) {
      toast.error('Something went wrong.');
    } finally {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, false);
        } else {
          if (temp.get(id)) {
            temp.set(id, false);
          }
        }
        return temp;
      });
    }
  };

  const handleDeleteNote = async (id: number) => {
    toast.success('Note deleted successfully.');
    try {
      setIsNoteDeleted(prev => {
        const temp = prev;
        if (temp.has(id)) {
          temp.set(id, true);
        } else {
          temp.set(id, true);
        }
        return temp;
      });
      await unconstrainedForecastService.deleteNote(id);
      // if (data) {
      //   toast.success("Note deleted successfully.");
      // }
    } catch (_error) {
      toast.error('Something went wrong.');
    } finally {
      setIsNoteDeleted(prev => {
        const temp = prev;
        if (temp.has(id)) {
          temp.set(id, false);
        } else {
          temp.set(id, false);
        }
        return temp;
      });
    }
  };

  const updateForecastAdjustment = (value: string, forecastMonthIndex: number) => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex].ndc
      : indirectNDCheading[indirectcurrentNDCIndex].ndc;

    value = value.replace(/,/g, '');

    // check if the value is numeric otherwise return
    if (value[0] === '0') {
      value = value.replace(/^0+/, '');
    }

    // check if the value is empty string, if then convert it to 0
    if (value === '' || value === '-') {
      value = '0';
    }

    if (!isNumeric(value)) return;

    let ndcIndex = -1;
    for (let i = 0; i < constrainedForecastEdit.length; i++) {
      if (constrainedForecastEdit[i].NDC11 === ndc) ndcIndex = i;
    }

    // get the particular forecast index details from the unconstraint forecast edit
    const ndcForecastMonthDetails = deepCopy(
      constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex]
    );

    // check and update the value type
    if (ndcUsingPercentage.has(ndc)) {
      const adjVal = Math.round(
        ((Number(value) + 100) * Number(ndcForecastMonthDetails.UnconstrainedForecast)) / 100
      ).toString();
      value = Math.max(Number(value), -100).toString();
      if (ndcValueTypeUsing.get(ndc) === 'saved') {
        ndcForecastMonthDetails.savedContraintForecastPerc = value;
        ndcForecastMonthDetails.savedConstraintForecast = adjVal;
      } else if (ndcValueTypeUsing.get(ndc) === 'old') {
        ndcForecastMonthDetails.oldPercentAdj = value;
        ndcForecastMonthDetails.OldConstrainedForecast = adjVal;
      } else {
        ndcForecastMonthDetails.percentadj = value;
        ndcForecastMonthDetails.ConstrainedForecast = adjVal;
      }
    } else {
      const percentVal = Math.max(
        Math.round((Number(value) * 100) / Number(ndcForecastMonthDetails.UnconstrainedForecast)) -
          100,
        -100
      ).toString();

      if (ndcValueTypeUsing.get(ndc) === 'saved') {
        ndcForecastMonthDetails.savedContraintForecastPerc = percentVal;
        ndcForecastMonthDetails.savedConstraintForecast = value;
      } else if (ndcValueTypeUsing.get(ndc) === 'old') {
        ndcForecastMonthDetails.oldPercentAdj = percentVal;
        ndcForecastMonthDetails.OldConstrainedForecast = value;
      } else {
        ndcForecastMonthDetails.percentadj = percentVal;
        ndcForecastMonthDetails.ConstrainedForecast = value;
      }
    }

    updateConstrainedForecastEdit(ndcForecastMonthDetails, ndcIndex, forecastMonthIndex);
  };

  const getValueForNdc = (forecastMonthIndex: number) => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    let ndcIndex = -1;
    for (let i = 0; i < constrainedForecastEdit.length; i++) {
      if (constrainedForecastEdit[i].NDC11 === ndc) ndcIndex = i;
    }
    const isPercent = ndcUsingPercentage.has(ndc);
    const fmtNum = (v: string | number | undefined | null) => {
      if (v === null || v === undefined) return '-';
      const n = Number(v);
      return !isNaN(n) ? n.toLocaleString('en-US') : String(v);
    };
    if (ndcValueTypeUsing.has(ndc)) {
      const type = ndcValueTypeUsing.get(ndc);
      if (type === 'saved') {
        return isPercent
          ? constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex].savedContraintForecastPerc || '0'
          : fmtNum(constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex].savedConstraintForecast);
      } else if (type === 'old') {
        return isPercent
          ? constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex].oldPercentAdj || '0'
          : fmtNum(constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex].OldConstrainedForecast);
      } else {
        return isPercent
          ? constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex].percentadj || '0'
          : fmtNum(constrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex].ConstrainedForecast);
      }
    }
    return '-';
  };

  const getNdcData = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    const filteredNdcsData = constrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return constrainedForecastEdit[0];

    const ndcData = filteredNdcsData[0];
    return ndcData;
  };

  const checkIfValueTypeRadioButtonIsDisabled = (valueType: 'saved' | 'old' | 'forecast') => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex].ndc
      : indirectNDCheading[indirectcurrentNDCIndex].ndc;

    const filteredNdcsData = constrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return true;

    const ndcData = filteredNdcsData[0];

    if (valueType === 'saved') {
      return !ndcData.saved_values;
    } else if (valueType === 'old') {
      return !ndcData.old_values_present;
    } else {
      return false;
    }
  };

  const checkIfPercentageEnabled = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    return ndcUsingPercentage.has(ndc) ? true : false;
  };

  const togglePercentageUsing = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex].ndc
      : indirectNDCheading[indirectcurrentNDCIndex].ndc;

    const tempNdcUsingPercentage = new Set(ndcUsingPercentage);

    if (ndcUsingPercentage.has(ndc)) {
      tempNdcUsingPercentage.delete(ndc);
    } else {
      tempNdcUsingPercentage.add(ndc);
    }
    setNdcUsingPercentage(tempNdcUsingPercentage);
  };

  const isValueTypeRadioButtonChecked = (valueType: valueTypeInterface) => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    return ndcValueTypeUsing.has(ndc) && ndcValueTypeUsing.get(ndc) === valueType ? true : false;
  };

  const changeNdcValueType = (valueType: 'saved' | 'old' | 'forecast') => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex].ndc
      : indirectNDCheading[indirectcurrentNDCIndex].ndc;
    const tempNdcValueTypeUsing: Map<string, 'saved' | 'old' | 'forecast'> = new Map(
      ndcValueTypeUsing
    );
    tempNdcValueTypeUsing.set(ndc, valueType);
    setNdcValueTypeUsing(tempNdcValueTypeUsing);
  };

  const getAdjForecast = (ndc: string, forecastMonthIndex: number) => {
    const filteredNdcsData = constrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return '-';

    const ndcData = filteredNdcsData[0];
    if (ndcValueTypeUsing.get(ndc) === 'saved') {
      if (ndcData.forecast_data.length > forecastMonthIndex) {
        return ndcData.forecast_data[forecastMonthIndex].savedConstraintForecast;
      }
    }
    if (ndcValueTypeUsing.get(ndc) === 'old') {
      if (ndcData.forecast_data.length > forecastMonthIndex) {
        return ndcData.forecast_data[forecastMonthIndex].OldConstrainedForecast;
      }
    }
    if (ndcData.forecast_data.length > forecastMonthIndex) {
      return ndcData.forecast_data[forecastMonthIndex].ConstrainedForecast;
    }
    return '-';
  };

  const isToggleVisible = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    const filteredNdcsData = constrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return true;

    const ndcData = filteredNdcsData[0];

    if (ndcData.saved_values) return true;
    else if (!ndcData.old_values_present) return true;
    else return false;
  };

  const handleCopyValues = (forecastIndex: number) => {
    const lastInputVal = getValueForNdc(forecastIndex);

    if (lastInputVal === '-' || lastInputVal === '') {
      toast.error('Empty value cannot be copied');
      return;
    }

    for (let i = forecastIndex + 1; i < 15; i++) {
      if (lastInputVal !== undefined) {
        updateForecastAdjustment(lastInputVal, i);
      }
    }
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex].ndc
      : indirectNDCheading[indirectcurrentNDCIndex].ndc;
    const temp_info = new Set([ndc + forecastIndex]);
    setCopyRowInfo(temp_info);
    toast.success(`Copied ${lastInputVal} to all rows.`);
  };

  const handleResetValues = (forecastIndex: number) => {
    const getConstrainedForecast = (
      data: unconstrainedForecastInterface,
      ndc: string,
      monthIndex: number
    ) => {
      let ndcIndex = -1;
      for (let i = 0; i < data.length; i++) {
        if (data[i].NDC11 === ndc) ndcIndex = i;
      }
      if (ndcValueTypeUsing.has(ndc)) {
        const type = ndcValueTypeUsing.get(ndc);
        if (type === 'saved') {
          return ndcUsingPercentage.has(ndc)
            ? data[ndcIndex].forecast_data[monthIndex].savedContraintForecastPerc
            : data[ndcIndex].forecast_data[monthIndex].savedConstraintForecast;
        } else if (type === 'old') {
          return ndcUsingPercentage.has(ndc)
            ? data[ndcIndex].forecast_data[monthIndex].oldPercentAdj
            : data[ndcIndex].forecast_data[monthIndex].OldConstrainedForecast;
        } else {
          return ndcUsingPercentage.has(ndc)
            ? data[ndcIndex].forecast_data[monthIndex].percentadj
            : data[ndcIndex].forecast_data[monthIndex].ConstrainedForecast;
        }
      }
      return '-';
    };

    if (forecastIndex < 15) {
      const ndc = isDirectMode
        ? directNDCheading[directcurrentNDCIndex].ndc
        : indirectNDCheading[indirectcurrentNDCIndex].ndc;

      for (let i = forecastIndex; i < 15; i++) {
        const oldForecast = getConstrainedForecast(oldconstrainedForecastEdit, ndc, i);

        updateForecastAdjustment(oldForecast, i);
      }

      setCopyRowInfo(new Set());
      toast.success('Restored value to default values');
    }
  };

  const handleDirectKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (index < 15) {
        directInputFocus.current[index + 1]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index > 0) {
        directInputFocus.current[index - 1]?.focus();
      }
    }
  };

  const handleIndirectKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (index < 15) {
        indirectInputFocus.current[index + 1]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index > 0) {
        indirectInputFocus.current[index - 1]?.focus();
      }
    }
  };

  const handleDirectFocus = (index: number) => {
    setDirectFocused(index);
  };

  const handleIndirectFocus = (index: number) => {
    setIndirectFocused(index);
  };

  const handleDirectBlur = () => {
    setDirectFocused(null);
  };

  const handleIndirectBlur = () => {
    setIndirectFocused(null);
  };

  useEffect(() => {
    const tempNdcValueTypeUsing: Map<string, 'saved' | 'old' | 'forecast'> = new Map();
    constrainedForecastEdit.forEach(ndcData => {
      if (ndcData.saved_values) {
        tempNdcValueTypeUsing.set(ndcData.NDC11, 'saved');
      } else if (ndcData.old_values_present) {
        tempNdcValueTypeUsing.set(ndcData.NDC11, 'old');
      } else {
        tempNdcValueTypeUsing.set(ndcData.NDC11, 'forecast');
      }
    });
    setNdcValueTypeUsing(tempNdcValueTypeUsing);
  }, [constrainedForecastEdit]);

  return {
    userModulesRoles,
    isConceptLoading,
    conceptDetails,
    contractDetails,
    forecastingMonthsEdit,
    directcurrentNDCIndex,
    indirectcurrentNDCIndex,
    isConstrainedForecastDataLoading,
    isConstrainedForecastNotesDataLoading,
    constrainedForecastEdit,
    isInvoiceSalesLoading,
    directNDCheading,
    indirectNDCheading,
    checked,
    singleSaveModal,
    saveAllModal,
    showSummary,
    isDirectMode,
    savingAll,
    savingSingle,
    savingForCategory,
    copyRowInfo,
    setSavingForCategory,
    isSavingSingle,
    setChecked,
    handleCcancel,
    handleDirectNDCTabChange,
    handleIndirectNDCTabChange,
    handleGetConceptDetails,
    getInvoiceSalesDetails,
    saveSingleConstrainedForecast,
    saveAllConstrainedForecast,
    saveForCategory,
    setShowSummary,
    setSingleSaveModal,
    setSaveAllModal,
    setIsDirectMode,
    handleNoteAdd,
    isConstrainedNoteSaving,
    ConstrainedForecastNotesData,
    getValueForNdc,
    checkIfValueTypeRadioButtonIsDisabled,
    isValueTypeRadioButtonChecked,
    changeNdcValueType,
    getNdcData,
    checkIfPercentageEnabled,
    togglePercentageUsing,
    getAdjForecast,
    updateForecastAdjustment,
    isToggleVisible,
    handleCopyValues,
    handleResetValues,
    handleDeleteNote,
    handleEditNote,
    setNoteData,
    removeNoteData,
    isNoteEdited,
    isNoteDeleted,
    directInputFocus,
    indirectInputFocus,
    directFocused,
    indirectFocused,
    handleDirectKeyPress,
    handleIndirectKeyPress,
    handleDirectFocus,
    handleIndirectFocus,
    handleDirectBlur,
    handleIndirectBlur,
  };
};
